// pages/dashboard/details/[type].jsx
import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../../../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../components/ui/card';
import { Input } from '../../../../../components/ui/input';
import { Button } from '../../../../../components/ui/button';
import { Badge } from '../../../../../components/ui/badge';
import {
  Search,
  Download,
  Filter,
  ArrowUpDown,
  Eye,
  FileText,
  Calendar,
  CreditCard,
  ShoppingBag,
  Users,
  ChefHat,
  Building,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { postRequest } from '../../../../../services/mainApp.service';
import { useSelector } from 'react-redux';

export default function DetailedReportPage() {
  const { type } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({
    status: 'all',
    paymentMode: 'all',
    orderMode: 'all'
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 1
  });

  const appStore = useSelector((state) => state.authReducer);

  useEffect(() => {
    fetchData();
  }, [type]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await postRequest("Dashboard_Order", {
        OperationId: 1,
        CompanyId: appStore.CompanyId,
        BranchId: appStore.userBranchList?.[0]?.BranchId || null,
        DateFrom: searchParams.get('from') || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        DateTo: searchParams.get('to') || new Date().toISOString().split('T')[0]
      });

      const fetchedData = response?.data?.Data || [];
      setData(fetchedData);

      // Update pagination with total items
      setPagination(prev => ({
        ...prev,
        totalItems: fetchedData.length,
        totalPages: Math.ceil(fetchedData.length / prev.pageSize),
        currentPage: 1
      }));

    } catch (error) {
      console.error('Error fetching detailed data:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: page }));
    }
  };

  const handlePageSizeChange = (size) => {
    const newPageSize = parseInt(size);
    const newTotalPages = Math.ceil(pagination.totalItems / newPageSize);
    const newCurrentPage = Math.min(pagination.currentPage, newTotalPages);

    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      totalPages: newTotalPages,
      currentPage: newCurrentPage
    }));
  };

  const exportToCSV = () => {
    const headers = getHeaders();
    const csvData = filteredData.map(item =>
      headers.map(header => {
        const value = item[header.key];
        // Handle special formatting for CSV
        if (header.key.includes('Amount') || header.key.includes('Price') || header.key.includes('Sale') || header.key.includes('NetSales')) {
          return `"Rs${parseFloat(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}"`;
        }
        if (header.key.includes('Date') || header.key.includes('DateTime')) {
          return `"${value ? new Date(value).toLocaleString() : ''}"`;
        }
        if (header.key.includes('Percentage')) {
          return `"${value || 0}%"`;
        }
        return `"${value || ''}"`;
      })
    );

    const csvContent = [
      headers.map(h => `"${h.label}"`).join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getHeaders = () => {
    switch (type) {
      case 'orders':
        return [
          { key: 'OrderNumber', label: 'Order Number' },
          { key: 'OrderStatus', label: 'Status' },
          { key: 'OrderDateTime', label: 'Date & Time' },
          { key: 'NetAmount', label: 'Net Amount' },
          { key: 'SubTotal', label: 'Subtotal' },
          { key: 'Gst', label: 'GST' },
          { key: 'DeliveryCharges', label: 'Delivery Charges' },
          { key: 'Discount', label: 'Discount' },
          { key: 'PaymentMode', label: 'Payment Mode' },
          { key: 'OrderMode', label: 'Order Mode' },
          { key: 'OrderSource', label: 'Order Source' },
          { key: 'BranchName', label: 'Branch' },
          { key: 'TerminalName', label: 'Terminal' },
          { key: 'ShiftName', label: 'Shift' }
        ];
      case 'products':
        return [
          { key: 'ProductName', label: 'Product' },
          { key: 'CategoryName', label: 'Category' },
          { key: 'Quantity', label: 'Quantity' },
          { key: 'SalesAmount', label: 'Amount' },
          { key: 'Percentage', label: 'Percentage' }
        ];
      case 'customers':
        return [
          { key: 'CustomerName', label: 'Name' },
          { key: 'PhoneNumber', label: 'Phone' },
          { key: 'OrderCount', label: 'Orders' },
          { key: 'NetSales', label: 'Total Spent' },
          { key: 'LastOrderDate', label: 'Last Order' },
          { key: 'CustomerType', label: 'Customer Type' }
        ];
      default:
        return [];
    }
  };

  const filteredData = useMemo(() => {
    let result = [...data];

    // Apply search
    if (searchTerm) {
      result = result.filter(item =>
        Object.values(item).some(value =>
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply filters
    if (filters.status !== 'all' && type === 'orders') {
      result = result.filter(item => item.OrderStatus === filters.status);
    }
    if (filters.paymentMode !== 'all' && type === 'orders') {
      result = result.filter(item => item.PaymentMode === filters.paymentMode);
    }
    if (filters.orderMode !== 'all' && type === 'orders') {
      result = result.filter(item => item.OrderMode === filters.orderMode);
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal === undefined || aVal === null) return 1;
        if (bVal === undefined || bVal === null) return -1;

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    // Update pagination totals for filtered data
    setPagination(prev => ({
      ...prev,
      totalItems: result.length,
      totalPages: Math.ceil(result.length / prev.pageSize),
      currentPage: result.length > 0 ? Math.min(prev.currentPage, Math.ceil(result.length / prev.pageSize)) : 1
    }));

    return result;
  }, [data, searchTerm, filters, sortConfig, type]);

  const paginatedData = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, pagination.currentPage, pagination.pageSize]);

  const getIcon = () => {
    switch (type) {
      case 'orders': return <ShoppingBag className="h-6 w-6" />;
      case 'products': return <FileText className="h-6 w-6" />;
      case 'customers': return <Users className="h-6 w-6" />;
      case 'staff': return <ChefHat className="h-6 w-6" />;
      case 'tables': return <Building className="h-6 w-6" />;
      case 'sales': return <CreditCard className="h-6 w-6" />;
      case 'overview': return <Eye className="h-6 w-6" />;
      case 'comparison': return <Calendar className="h-6 w-6" />;
      default: return <FileText className="h-6 w-6" />;
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'orders': return 'Orders Detailed Report';
      case 'products': return 'Products Detailed Report';
      case 'customers': return 'Customers Detailed Report';
      case 'staff': return 'Staff Performance Report';
      case 'tables': return 'Table Utilization Report';
      case 'sales': return 'Sales Detailed Report';
      case 'overview': return 'Overview Report';
      case 'comparison': return 'Comparison Report';
      default: return 'Detailed Report';
    }
  };

  const renderCell = (item, header) => {
    const value = item[header.key];

    switch (header.key) {
      case 'OrderStatus':
        return (
          <Badge variant={
            value === 'Paid' ? 'default' :
              value === 'Confirmed' ? 'secondary' :
                'destructive'
          }>
            {value || 'Unknown'}
          </Badge>
        );
      case 'CustomerType':
        return (
          <Badge variant={value === 'New' ? 'default' : 'secondary'}>
            {value || 'Regular'}
          </Badge>
        );
      case 'NetAmount':
      case 'SubTotal':
      case 'Gst':
      case 'DeliveryCharges':
      case 'Discount':
      case 'SalesAmount':
      case 'NetSales':
        return `Rs${parseFloat(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'OrderDateTime':
      case 'LastOrderDate':
        return value ? new Date(value).toLocaleString() : '-';
      case 'Percentage':
        return `${parseFloat(value || 0).toFixed(2)}%`;
      case 'OrderCount':
      case 'Quantity':
        return value || 0;
      case 'PaymentMode':
        return value || 'Not specified';
      default:
        return value || '-';
    }
  };

  const renderSkeleton = () => {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-lg bg-muted animate-pulse" />
            <div>
              <div className="h-8 w-48 bg-muted animate-pulse rounded" />
              <div className="h-4 w-64 mt-2 bg-muted animate-pulse rounded" />
            </div>
          </div>
          <div className="flex gap-2">
            <div className="h-10 w-24 bg-muted animate-pulse rounded" />
            <div className="h-10 w-24 bg-muted animate-pulse rounded" />
          </div>
        </div>

        <div className="h-24 bg-muted animate-pulse rounded-lg" />

        <div className="space-y-2">
          <div className="h-12 bg-muted animate-pulse rounded-lg" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        {renderSkeleton()}
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>

          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
            {getIcon()}
          </div>
          <div>
            <h1 className="text-3xl font-bold">{getTitle()}</h1>
            <p className="text-muted-foreground">
              {filteredData.length} records found • Page {pagination.currentPage} of {pagination.totalPages}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={exportToCSV}
            disabled={filteredData.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={fetchData} variant="secondary">
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={`Search ${type}...`}
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-2">
              {type === 'orders' && (
                <>
                  <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="Paid">Paid</SelectItem>
                      <SelectItem value="Confirmed">Confirmed</SelectItem>
                      <SelectItem value="Cancel">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filters.paymentMode} onValueChange={(value) => setFilters(prev => ({ ...prev, paymentMode: value }))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Payment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Payments</SelectItem>
                      <SelectItem value="CASH">Cash</SelectItem>
                      <SelectItem value="CARD">Card</SelectItem>
                      <SelectItem value="null">Not specified</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filters.orderMode} onValueChange={(value) => setFilters(prev => ({ ...prev, orderMode: value }))}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Order Mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Modes</SelectItem>
                      <SelectItem value="Pickup">Pickup</SelectItem>
                      <SelectItem value="Delivery">Delivery</SelectItem>
                      <SelectItem value="DineIn">Dine In</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}

              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setFilters({ status: 'all', paymentMode: 'all', orderMode: 'all' });
                  setPagination(prev => ({ ...prev, currentPage: 1 }));
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Detailed Data</CardTitle>
            <div className="text-sm text-muted-foreground">
              Showing {paginatedData.length} of {filteredData.length} filtered records
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {paginatedData.length > 0 ? (
            <>
              <div className="rounded-md border custom-scrollbar" style={{ maxHeight: '600px', overflow: 'auto' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      {getHeaders().map((header, index) => (
                        <TableHead key={index}>
                          <Button
                            variant="ghost"
                            onClick={() => handleSort(header.key)}
                            className="hover:bg-transparent p-0 h-auto font-bold"
                          >
                            {header.label}
                            <ArrowUpDown className="ml-2 h-4 w-4" />
                          </Button>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.map((item, index) => (
                      <TableRow key={index}>
                        {getHeaders().map((header, headerIndex) => (
                          <TableCell key={headerIndex}>
                            {renderCell(item, header)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center justify-between px-2 py-4">
                <div className="flex items-center space-x-2">
                  <p className="text-sm font-medium">Rows per page</p>
                  <Select
                    value={pagination.pageSize.toString()}
                    onValueChange={handlePageSizeChange}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue placeholder={pagination.pageSize} />
                    </SelectTrigger>
                    <SelectContent side="top">
                      {[10, 20, 30, 40, 50].map((pageSize) => (
                        <SelectItem key={pageSize} value={pageSize.toString()}>
                          {pageSize}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-6 lg:space-x-8">
                  <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      className="hidden h-8 w-8 p-0 lg:flex"
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.currentPage === 1}
                    >
                      <span className="sr-only">Go to first page</span>
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      className="h-8 w-8 p-0"
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                    >
                      <span className="sr-only">Go to previous page</span>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      className="h-8 w-8 p-0"
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                    >
                      <span className="sr-only">Go to next page</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      className="hidden h-8 w-8 p-0 lg:flex"
                      onClick={() => handlePageChange(pagination.totalPages)}
                      disabled={pagination.currentPage === pagination.totalPages || pagination.totalPages === 0}
                    >
                      <span className="sr-only">Go to last page</span>
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="h-12 w-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold">No data found</h3>
              <p className="text-muted-foreground">
                {data.length === 0
                  ? `No ${type} data available for the selected period.`
                  : 'Try adjusting your search or filters'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Records</p>
                <p className="text-2xl font-bold">{filteredData.length}</p>
              </div>
              <FileText className="h-8 w-8 text-primary/20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Value</p>
                <p className="text-2xl font-bold">
                  ${filteredData.reduce((sum, item) => {
                    const value = item.NetAmount || item.SalesAmount || item.NetSales || 0;
                    return sum + (typeof value === 'number' ? value : 0);
                  }, 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <CreditCard className="h-8 w-8 text-green-500/20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Current Page</p>
                <p className="text-2xl font-bold">
                  {pagination.currentPage} / {pagination.totalPages}
                </p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500/20" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}